import { Button, Center, Text, TextInput, Group } from "@mantine/core";
import { useEffect, useState, useMemo } from "react";
import { AddOrganisationForm, EditOrganisationForm } from "./forms/modals/OrganisationForms";
import { OrganisationType } from "../../types/Organisation";
import { SortableTable, ColumnDef } from "../SortableTable";
import { IconSearch, IconPlus } from "@tabler/icons-react";
import styles from './OrganisationTable.module.css';
import { useTranslation } from "../../hooks/useTranslation";
import useTransientStore from "../../data/transcientStore";

interface OrganisationTableProps {
    organisationData: OrganisationType[];
    refreshOrganisations: () => void;
}

export default function OrganisationTable({ organisationData, refreshOrganisations }: OrganisationTableProps) {
    const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
    const addrIdFromStore = selectedAddressSystem?.id || 0;

    const { getTranslation } = useTranslation({ pageId: 310, addressSystemId: addrIdFromStore || 0, autoLoad: true });
    const [orgSearch, setOrgSearch] = useState<string>('');
    const [selectedID, setSelectedID] = useState<number>(-1);
    const [selectedOrganisation, setSelectedOrganisation] = useState<OrganisationType>({
        id: 0,
        name: '',
    });
    const [addOrganisationOpened, setAddOrganisationOpened] = useState(false);
    const [editOrganisationOpened, setEditOrganisationOpened] = useState(false);
    const [highlightedRowId, setHighlightedRowId] = useState<number | null>(null);
    const pageSize = 100;

    useEffect(() => {
        const organisation = organisationData.find((org) => org.id === selectedID);
        if (organisation) {
            setSelectedOrganisation(organisation);
        }

    }, [orgSearch, selectedID, organisationData])

    const searchedOrganisation = useMemo(() => 
        organisationData
            .filter((item) =>
                item.name.toLowerCase().includes(orgSearch.toLowerCase())
            ),
        [organisationData, orgSearch]
    );

    const columns: ColumnDef<OrganisationType>[] = [
        { header: getTranslation("LABEL_ORG_TABLE_ID") || "Id", accessor: 'id', sortable: true, width: 200 },
        {
            header: getTranslation("LABEL_ORG_TABLE_ORGANISATION") || "Organisation", accessor: 'name', sortable: true, width: 800,
        },
    ];

        // useEffect(() => {
    //     function updatePageSize() {
    //         const availableHeight = window.innerHeight - 300;
    //         const rows = Math.max(3, Math.floor(availableHeight / 48));
    //         setPageSize(rows);
    //     }
    //     updatePageSize();
    //     window.addEventListener('resize', updatePageSize);
    //     return () => window.removeEventListener('resize', updatePageSize);
    // }, []);
        
    return (
        <div className={styles.container}>
            <>
                <AddOrganisationForm
                    opened={addOrganisationOpened}
                    open={() => setAddOrganisationOpened(true)}
                    close={() => setAddOrganisationOpened(false)}
                    onSuccess={refreshOrganisations}
                    allOrganisations={organisationData} />

                <EditOrganisationForm
                    opened={editOrganisationOpened}
                    open={() => setEditOrganisationOpened(true)}
                    close={() => { setEditOrganisationOpened(false); setHighlightedRowId(null); }}
                    selectedOrg={selectedOrganisation}
                    onSuccess={refreshOrganisations}
                    allOrganisations={organisationData} />
            </>

            <>
                <Group mb="md" justify="space-between" align="center" className={styles.headerGroup}>
                    <TextInput
                        value={orgSearch}
                        onChange={(event) => { setOrgSearch(event.currentTarget.value) }}
                        placeholder={getTranslation("PLACEHOLDER_SEARCH_ORGANISATIONS") || "Search organisations..."}
                        leftSection={<IconSearch size={18} color="#009DF0" />}
                        className={styles.searchInput}
                    />
                    <Button onClick={() => setAddOrganisationOpened(true)} rightSection={<IconPlus size={16} />}>
                        {getTranslation("LABEL_ORG_TABLE_ADD_ORGANISATION") || "Add Organisation"}
                    </Button>
                </Group>

                <SortableTable
                    data={searchedOrganisation}
                    columns={columns}
                    pageSize={pageSize}
                    rowKey={(row) => row.id}
                    rowStyle={(row) => 
                        highlightedRowId === row.id 
                            ? { backgroundColor: '#e3f0ff' }
                            : {}
                    }
                    actionColumns={[
                        {
                            label: getTranslation("LABEL_ORG_TABLE_EDIT") || "Edit",
                            width: 50,
                            render: (row) => (
                                <Button onClick={() => { 
                                    setSelectedID(row.id); 
                                    setHighlightedRowId(row.id);
                                    setEditOrganisationOpened(true); 
                                }}>
                                    {getTranslation("LABEL_ORG_TABLE_EDIT") || "Edit"}
                                </Button>
                            )
                        }
                    ]}
                />
                {searchedOrganisation.length === 0 && (
                    <Center mt="xl">
                        <Text>{getTranslation("LABEL_ORG_TABLE_NO_RECORDS") || "No records."}</Text>
                    </Center>
                )}
            </>
        </div>
    )
}